import { trace } from '@opentelemetry/api';

import { logError } from './logger.mjs';
import getCacheStrategy from './server/getCacheStrategy';

const getGraphqlOperationTag = options => {
  if (!options?.body) return 'unknown';
  try {
    const { operationName } = JSON.parse(options.body);
    return operationName || 'unknown';
  } catch {
    return 'unknown';
  }
};

const logFetchFailure = (error, url, options, causeErr) => {
  logError(
    error,
    { graphql_operation: getGraphqlOperationTag(options) },
    {
      fetch_url: String(url),
      underlying_error: causeErr?.name,
      underlying_message: causeErr?.message,
    }
  );
};

const getDebugLabel = (url, options) => {
  const { method, body } = options;

  if (body) {
    try {
      const bodyObject = JSON.parse(body || '{}');

      const isQuery = bodyObject.query?.startsWith('query ');
      const isMutation = bodyObject.query?.startsWith('mutation ');
      const operationName = bodyObject.operationName;

      if (isQuery && operationName) {
        return `query ${operationName}`;
      }

      if (isMutation && operationName) {
        return `mutation ${operationName}`;
      }
    } catch {
      return `${method} ${url}`;
    }
  }

  return `${method} ${url}`;
};

export const fetchWithRetry = async (url, options) => {
  const debugLabel = getDebugLabel(url, options);
  const { cache = 'force-cache', revalidate = 3600 } = options;

  // Determine cache strategy based on request context
  const cacheStrategy = getCacheStrategy(options, cache);

  try {
    return await trace
      .getTracer('GraphQL request')
      .startActiveSpan(debugLabel, async span => {
        try {
          return await fetch(url, {
            ...options,
            cache: cacheStrategy,
            revalidate,
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            span.addEvent('Request aborted');
            return null;
          }

          const error = new Error(
            `fetchWithRetry: Request error: ${debugLabel}`,
            {
              cause: err,
            }
          );

          logFetchFailure(error, url, options, err);

          span.recordException(error);
          span.setStatus({ code: 2, message: error.message });
          throw error;
        } finally {
          span.end();
        }
      });
  } catch (err) {
    if (err.name === 'AbortError') {
      return null;
    }

    if (/ETIMEDOUT|ECONNRESET|Network Error/.test(err.message)) {
      try {
        return await trace
          .getTracer('GraphQL request')
          .startActiveSpan(debugLabel, async span => {
            try {
              return await fetch(url, {
                ...options,
                cache: cacheStrategy,
                revalidate,
              });
            } catch (err) {
              if (err.name === 'AbortError') {
                span.addEvent('Request aborted');
                return null;
              }

              const error = new Error(
                `fetchWithRetry: Request error: ${debugLabel}`,
                {
                  cause: err,
                }
              );

              logFetchFailure(error, url, options, err);

              span.recordException(error);
              span.setStatus({ code: 2, message: error.message });
              throw error;
            } finally {
              span.end();
            }
          });
      } catch (retryErr) {
        const retryError = new Error(
          `fetchWithRetry: Request error: ${debugLabel}`,
          {
            cause: retryErr,
          }
        );

        throw retryError;
      }
    }

    throw err;
  }
};
